import api from './api';

const apiBaseUrl = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');

const hasPendingTicketImages = (images = []) => (
  Array.isArray(images) && images.some((image) => (
    image?.file || (typeof File !== 'undefined' && image instanceof File)
  ))
);

const buildTicketMultipartData = (payload = {}) => {
  const images = Array.isArray(payload.images) ? payload.images : [];
  const requestPayload = { ...payload };
  delete requestPayload.images;

  if (!hasPendingTicketImages(images)) {
    return requestPayload;
  }

  const formData = new FormData();
  formData.append('payload', JSON.stringify(requestPayload));
  images.forEach((image) => {
    formData.append('images', image.file || image);
  });

  return formData;
};

const multipartConfig = (requestData) => (
  requestData instanceof FormData
    ? {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    : undefined
);

const ticketService = {
  getUpdateRecords: async params => {
    try { return (await api.get('/tickets/update-records', { params })).data; }
    catch (error) { return error.response?.data || { success: false, message: error.message }; }
  },
  getUpdateRecord: async (id, module) => {
    try { return (await api.get(`/tickets/update-records/${id}`, { params: { module } })).data; }
    catch (error) { return error.response?.data || { success: false, message: error.message }; }
  },
  decideRecordUpdate: async (id, data) => {
    try { return (await api.post(`/tickets/${id}/record-decision`, data)).data; }
    catch (error) { return error.response?.data || { success: false, message: error.message }; }
  },
  getProviderOptions: async (params) => {
    try {
      const response = await api.get('/tickets/provider-options', { params });
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  updateCollaboration: async (id, data) => {
    try {
      const requestData = buildTicketMultipartData(data);
      const response = await api.put(`/tickets/${id}/collaboration`, requestData, multipartConfig(requestData));
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },
  updateTicketRequest: async (id, data) => {
    try {
      const response = await api.put(`/tickets/${id}/request`, data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getRequestProviderOptions: async (ticketId, search) => {
    try {
      const response = await api.get(`/tickets/${ticketId}/request-options`, { params: { search } });
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getRecordCreationOptions: async (level, ticketId) => {
    try {
      const response = await api.get(ticketId ? `/tickets/${ticketId}/request-options` : `/tickets/${level}-options`);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  updateRecordCreationRequest: async (id, level, action, data) => {
    try {
      const response = await api.post(`/tickets/${id}/${level}-${action}`, data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getTickets: async (params) => {
    try {
      const response = await api.get('/tickets', { params });
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  createTicket: async (data) => {
    try {
      const response = await api.post('/tickets', data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getMerchantTickets: async (merchantId) => {
    try {
      const response = await api.get(`/tickets/merchant/${merchantId}`);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getCostingRateProviders: async (params) => {
    try {
      const response = await api.get('/tickets/costing-rate-providers', { params });
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getTicketById: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}`);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  updateTicket: async (id, data) => {
    try {
      const response = await api.put(`/tickets/${id}`, data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getAssignees: async () => {
    try {
      const response = await api.get('/tickets/assignees');
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getWorkflow: async () => {
    try {
      const response = await api.get('/tickets/workflow');
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  updateWorkflow: async (data) => {
    try {
      const response = await api.put('/tickets/workflow', data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  syncMerchantTickets: async (merchantId) => {
    try {
      const response = await api.post(`/tickets/merchant/${merchantId}/sync`);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  performAction: async (id, data) => {
    try {
      const requestData = buildTicketMultipartData(data);
      const response = await api.post(`/tickets/${id}/action`, requestData, multipartConfig(requestData));
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  addComment: async (id, message, images = []) => {
    try {
      const requestData = buildTicketMultipartData({ message, images });
      const response = await api.post(`/tickets/${id}/comments`, requestData, multipartConfig(requestData));
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  decideCostingRateChange: async (id, data) => {
    try {
      const response = await api.post(`/tickets/${id}/costing-rate-decision`, data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  decideProviderChange: async (id, data) => {
    try {
      const response = await api.post(`/tickets/${id}/provider-change-decision`, data);
      return response.data;
    } catch (error) {
      return error.response?.data || { success: false, message: error.message };
    }
  },

  getTicketImageUrl: (image) => {
    if (!image?.file_path) return '#';
    if (/^https?:\/\//i.test(image.file_path)) return image.file_path;
    return `${apiBaseUrl}${image.file_path}`;
  }
};

export default ticketService;
